<template>
  <div>
    <field-renderer
      v-for="(subField, subIndex) in nonGameTypeFields"
      :key="`loyalty-field-${subIndex}`"
      :field="subField"
      :item="item"
      :loading="loading"
    />

    <v-divider class="my-3" />
    <label>Loyalty Rate, percent from bet in EUR</label>

    <div class="loyalty-rule-row mt-4" style="padding-right: 45px;">
      <div class="loyalty-rule-col">
        <v-select
          value="Default"
          :items="['Default']"
          :disabled="loading"
          readonly
          hide-details
          dense
          outlined
        />
      </div>
      <div class="loyalty-rule-col">
        <v-text-field
          :value="item[DEFAULT_LOYALTY_KEY]"
          @input="handleUpdateLoyaltyValue(DEFAULT_LOYALTY_KEY, $event)"
          type="number"
          hide-details
          label="Rate, EUR"
          suffix="%"
          dense
          outlined
          :disabled="loading"
        />
      </div>

    </div>
    <div class="caption grey--text text--darken-1">
      Default rate for all Game types
    </div>

    <div class="mt-4">
      <div class="loyalty-section__header-row">
        <div>
          <div class="font-weight-bold subtitle-2">GAME TYPE RULES</div>
          <div class="caption grey--text text--darken-1">
            Overrides the default rule for selected game types.
          </div>
        </div>
        <v-btn
          color="primary"
          outlined
          small
          :disabled="loading"
          @click="handleAddLoyaltyGametype"
        >
          <v-icon small left>mdi-plus</v-icon>
          ADD RULE
        </v-btn>
      </div>
      <div
        v-for="field in loyaltyFields"
        :key="field.key"
        class="loyalty-rule-row mt-4"
      >
        <div class="loyalty-rule-col">
          <v-select
            :value="getGameTypeFromKey(field.key)"
            @input="handleUpdateGameType(field, $event)"
            :items="getAvailableGameTypesForField(field)"
            :disabled="loading"
            label="Game type"
            hide-details
            dense
            outlined
          />
        </div>
        <div class="loyalty-rule-col">
          <v-text-field
            :value="item[field.key]"
            @input="handleUpdateLoyaltyValue(field.key, $event)"
            type="number"
            hide-details
            label="Rate, EUR"
            suffix="%"
            dense
            outlined
            :disabled="loading || !isGameTypeSelected(field.key)"
          />
        </div>
        <v-btn
          icon
          small
          :disabled="loading"
          class="mb-2"
          @click="handleRemoveLoyaltyGametype(field.key)"
        >
          <v-icon>mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import FieldRenderer from './FieldRenderer.vue'

  export default {
    name: 'LoyaltySettings',
    components: {
      FieldRenderer,
    },
    props: {
      item: {
        type: Object,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      fields: {
        type: Array,
        required: true,
      },
      gameTypes: {
        type: Array,
        default: () => [],
      },
      entityName: {
        type: String,
        default: '',
      },
    },
    data: () => ({
      DEFAULT_LOYALTY_KEY: 'loyalty.gametype.default',
      LOYALTY_BASEKEY: 'loyalty.gametype.',
      loyaltyFields: [],
    }),
    computed: {
      gameTypesList () {
        return this.gameTypes.map(gt => ({ value: gt, text: _.startCase(gt) }))
      },
      loyaltyKeys () {
        return Object.keys(this.item).filter(key =>
          key.startsWith(this.LOYALTY_BASEKEY)
          && this.item[key] != null
          && key !== this.DEFAULT_LOYALTY_KEY
        )
      },
      nonGameTypeFields () {
        return this.fields.filter(f => !f.key.startsWith(this.LOYALTY_BASEKEY))
      },
      isAllGameTypesSelected () {
        return this.loyaltyFields.every(field => this.isGameTypeSelected(field.key))
      },
    },
    watch: {
      item: {
        immediate: true,
        handler () {
          this.initLoyaltyFields()
        },
      },
    },
    mounted () {
      this.initLoyaltyFields()
    },
    methods: {
      initLoyaltyFields () {
        this.loyaltyFields = (this.loyaltyKeys || []).map(key => ({
          label: key.replace(this.LOYALTY_BASEKEY, ''),
          key,
          type: 'number',
        }))
      },
      getGameTypeFromKey (key) {
        return key.startsWith(this.LOYALTY_BASEKEY)
          ? key.replace(this.LOYALTY_BASEKEY, '')
          : null
      },
      isGameTypeSelected (key) {
        if (!key.startsWith(this.LOYALTY_BASEKEY)) return false
        const gameType = key.replace(this.LOYALTY_BASEKEY, '')
        return this.gameTypes.includes(gameType)
      },
      getAvailableGameTypesForField (currentField) {
        const current = this.getGameTypeFromKey(currentField.key)

        const selected = new Set(
          this.loyaltyFields
            .filter(f => f !== currentField)
            .map(f => this.getGameTypeFromKey(f.key))
            .filter(Boolean)
        )

        return this.gameTypesList.filter(gt => {
          return gt.value === current || !selected.has(gt.value)
        })
      },
      handleAddLoyaltyGametype () {
        const newField = {
          label: 'Loyalty',
          key: 'loyalty',
          type: 'number',
        }

        const exists = this.loyaltyFields.some(
          field => field.key === newField.key
        )

        if (exists) return

        this.loyaltyFields.push(newField)
        this.$emit('validation-change', false)
      },
      handleUpdateGameType (field, gameType) {
        const oldKey = field.key
        const newKey = `${this.LOYALTY_BASEKEY}${gameType}`
        const newLabel = gameType

        this.$set(field, 'key', newKey)
        this.$set(field, 'label', newLabel)

        const updatedItem = cloneDeep(this.item)
        const hasOld = _.has(updatedItem, oldKey)
        this.$set(updatedItem, newKey, hasOld ? updatedItem[oldKey] : 0)

        if (oldKey !== newKey && hasOld) this.$delete(updatedItem, oldKey)

        console.log(updatedItem)
        this.$emit('update-item', updatedItem)
        this.$emit('validation-change', this.isAllGameTypesSelected)
      },
      handleRemoveLoyaltyGametype (key) {
        this.loyaltyFields = this.loyaltyFields.filter(f => f.key !== key)
        const updatedItem = cloneDeep(this.item)
        this.$set(updatedItem, key, null)

        this.$emit('update-item', updatedItem)
        this.$emit('validation-change', this.isAllGameTypesSelected)
      },
      handleUpdateLoyaltyValue (key, value) {
        const updatedItem = cloneDeep(this.item)
        this.$set(updatedItem, key, value ? Number(value) : 0)
        this.$emit('update-item', updatedItem)
      },
    },
  }
</script>

<style scoped>
  .loyalty-section__header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .loyalty-rule-row {
    display: flex;
    align-items: flex-end;
    gap: 16px;
  }

  .loyalty-rule-col {
    flex: 1;
  }
</style>

